import React, { useEffect } from 'react';
import { Button, Alert } from '@mui/material';
import { Stack } from '@mui/system';
import { Header } from 'components/home';
import { ItemForm } from 'components/shared/item_form';
import { PageTitle } from 'components/shared/page_title';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import useCifaStore from 'stores/cifa/cifa.store';
import { CifaStatusEnum, StatusCifaTextEnum } from 'enum/cifaStatus.enum';

const getStatusText = (status: CifaStatusEnum) => {
    switch (status) {
        case CifaStatusEnum.NOT_STARTED:
            return StatusCifaTextEnum.NOT_STARTED;
        case CifaStatusEnum.PENDING:
            return StatusCifaTextEnum.PENDING;
        case CifaStatusEnum.WAITING_FOR_APPROVAL:
            return StatusCifaTextEnum.WAITING_FOR_APPROVAL;
        case CifaStatusEnum.APPROVED:
            return StatusCifaTextEnum.APPROVED;
        case CifaStatusEnum.REJECTED:
            return StatusCifaTextEnum.REJECTED;
        default:
            return StatusCifaTextEnum.NOT_STARTED;
    }
}

const ViewCifa: React.FC = () => {
    const { cifa, getCifa } = useCifaStore();
    const navigate = useNavigate();
    const location = useLocation();

    // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
    const match = /cifa-(\d{4})/.exec(location.pathname);
    const anoReferencia = match ? Number(match[1]) : 2026;

    const handleBack = () => {
        navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
    };

    useEffect(() => {
        if (!cifa?.uuid) {
            handleBack();
            return;
        }
        getCifa(cifa?.institution_uuid, cifa?.ano_referencia);
        // eslint-disable-next-line
    }, []);


    // Lista de abas preenchidas (protege contra cifa undefined)
    const filledItems = [
        {
            number: 1,
            status: cifa?.status_aba_1,
            title: 'Cumprimento do Termo Escolar',
            subtitle: 'Visualize os dados sobre o cumprimento dos requisitos do Termo de Adesão pelo Município.',
            path: ROUTES[`VIEW_COMPLIANCE_TERM_${anoReferencia}` as keyof typeof ROUTES] as string,
        },
        {
            number: 2,
            status: cifa?.status_aba_2,
            title: 'Assistência Técnica e Extensão Rural',
            subtitle: 'Visualize as informações sobre os atendimentos ATER realizados no Município.',
            path: ROUTES[`VIEW_TECH_ASSIST_${anoReferencia}` as keyof typeof ROUTES] as string,
        },
        {
            number: 3,
            status: cifa?.status_aba_3,
            title: 'Alimentação Escolar',
            subtitle: 'Visualize os dados sobre a aquisição de Alimentação Escolar no Município.',
            path: ROUTES[`VIEW_SCHOOL_FEEDING_${anoReferencia}` as keyof typeof ROUTES] as string,
        },
        {
            number: 4,
            status: cifa?.status_aba_4,
            title: 'Alimentação Escolar Aquisições',
            subtitle: 'Visualize os dados sobre a aquisição de Alimentação Escolar no Município.',
            path: ROUTES[`VIEW_SCHOOL_FEEDING_ACQUISITIONS_${anoReferencia}` as keyof typeof ROUTES] as string,
        },
        {
            number: 5,
            status: cifa?.status_aba_5,
            title: 'Recurso Financeiro Aplicado',
            subtitle: 'Veja as informações sobre os valores destinados e aplicados à dinamização da agricultura familiar no Município.',
            path: ROUTES[`VIEW_FINANCIAL_RESOURCE_${anoReferencia}` as keyof typeof ROUTES] as string,
        },
    ].filter(item => !!item.status);

    return (
        <MenuProvider>
            <TopNavBar />
            <Header title={`CIFA - Visualizar Município - ${anoReferencia}`} showBackButton={true} />

            <PageTitle
                title={`Visualização de Município - CIFA ${anoReferencia}`}
                subtitle='Veja o resumo das informações preenchidas em cada etapa do questionário.'
            />

            <Stack marginX='4rem' spacing='2rem'>
                {filledItems.length === 0 && (
                    <Alert severity="info" sx={{ mt: 10 }}>
                        Para visualizar o CIFA, é necessário preencher pelo menos uma etapa do questionário.
                    </Alert>
                )}

                {filledItems.map(item => {
                    const statusText = getStatusText(item.status);
                    const showItem =
                        item.status === CifaStatusEnum.PENDING ||
                        item.status === CifaStatusEnum.WAITING_FOR_APPROVAL ||
                        item.status === CifaStatusEnum.APPROVED ||
                        item.status === CifaStatusEnum.REJECTED;

                    return showItem ? (
                        <ItemForm
                            key={item.number}
                            number={item.number}
                            title={item.title}
                            subtitle={item.subtitle}
                            status={statusText}
                            pathTo={item.path}
                        />
                    ) : null;
                })}

                <Stack
                    direction='row'
                    spacing={2}
                    justifyContent='center'
                    alignItems='center'
                    marginTop='2rem'
                >
                    <Button variant='text' color='primary' onClick={handleBack}>
                        Voltar
                    </Button>
                </Stack>
            </Stack>
        </MenuProvider>
    );
};

export default ViewCifa;
